import { PencilIcon } from 'components/icons';
import AddressModal from 'components/modals/address/AddressModal';
import { useModal } from 'components/modals/useModal';
import { useInRecordSection, useSectionWideClass } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import { cn } from 'utils/cn';
import get from 'lodash/get';
import { ComponentProps, useCallback, useMemo } from 'react';
import { Controller, FieldValues, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { AddressDto } from 'types/contacts';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { getAddress, getOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { useCustomFields } from '../custom-fields/useCustomFields';
import { CommonInputProps } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useMemoizedRules } from './useMemoizedRules';

interface Props<T extends FieldValues> extends CommonInputProps<T> {
    classifiers: Record<string, ClassifierData>;
    readonly?: boolean;
    handleCloseModal?: () => void;
    customFields?: CustomField[];
    isBillingAddress?: boolean;
    sourceAddressForCopy?: AddressDto | null;
    getSourceAddressForCopy?: () => AddressDto | null;
    partyType?: 'PERSON' | 'COMPANY';
    hasPrivilege?: boolean;
    clearable?: boolean;
}

/**
 * An address's Edit in the new design, in read and in edit: a pencil set after the address's last word. The
 * negative margin keeps the 24px button inside the text's 20px line, wherever `align-middle` puts it.
 */
export const AddressPencilButton = ({ className, ...props }: ComponentProps<typeof Button>) => (
    <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label={t('edit')}
        className={cn('text-muted-foreground hover:text-primary -my-1 ml-1 align-middle', className)}
        {...props}
    >
        <PencilIcon className="size-3.5" />
    </Button>
);

const AddressInput = <T extends FieldValues>({
    name,
    disabled,
    required,
    label,
    rightLabelComponent,
    wrapperClassName,
    validate,
    control,
    classifiers,
    readonly = false,
    handleCloseModal,
    customFields,
    isBillingAddress,
    sourceAddressForCopy,
    getSourceAddressForCopy,
    partyType,
    hasPrivilege = true,
    clearable = false,
}: Props<T>) => {
    const watchedValue = useWatch({ control, name });
    const rules = useMemoizedRules({ required, validate });

    // Derive merged custom fields at render time — previously computed in a useEffect that mirrored
    // watchedValue into local state, which was a redundant render-then-render-again loop.
    const stateCustomFields = useMemo<CustomField[]>(() => {
        const watchedCustomFields = (get(watchedValue, 'customFields') ?? []) as CustomField[];
        return watchedCustomFields.map((formCustomField) => {
            const existingItemA = customFields?.find((dtoCustomField) => dtoCustomField.id === formCustomField.id);
            if (existingItemA) {
                return {
                    ...existingItemA,
                    value: !formCustomField.isMultivalue ? formCustomField.value : null,
                } as CustomField;
            }
            return formCustomField;
        });
    }, [watchedValue, customFields]);

    const { openModal, closeModal } = useModal();
    // In a record section a stored address has a row of its own, as an address seldom fits a cell, and takes the
    // box of a read value, in its text: 36px for one line and a line more for each the address wraps onto, over
    // the same width. Its Edit is the read row's pencil, after its last word as there, so both wrap alike.
    const inSection = useInRecordSection();
    const wideClass = useSectionWideClass(control, name);

    const { customFields: addressCustomFields, initialCustomFieldsObject: initialAddressCustomFields } =
        useCustomFields({
            entityType: EntityType.PARTY_ADDRESS,
            fields: stateCustomFields,
        });

    const openAddressModal = useCallback(
        (onChange: (event: AddressDto | React.ChangeEvent<Element> | null) => void): void => {
            const resolvedSourceAddress = getSourceAddressForCopy ? getSourceAddressForCopy() : sourceAddressForCopy;
            openModal(
                <AddressModal
                    onChange={(value) => {
                        onChange(value);
                    }}
                    initialData={{
                        additionalInformation: get(watchedValue, 'additionalInformation') ?? '',
                        address1: get(watchedValue, 'address1') ?? '',
                        address2: get(watchedValue, 'address2') ?? '',
                        cityName: get(watchedValue, 'cityName') ?? '',
                        countryCode:
                            (
                                getOptionsFromClassifier(classifiers['COUNTRY']) as {
                                    label: string;
                                    value: string;
                                }[]
                            ).find((val) => val.value === get(watchedValue, 'countryCode')) ?? null,
                        zipCode: get(watchedValue, 'zipCode') ?? '',
                        ...initialAddressCustomFields,
                    }}
                    onClose={handleCloseModal ? handleCloseModal : closeModal}
                    classifiers={classifiers}
                    customFields={addressCustomFields}
                    isBillingAddress={isBillingAddress}
                    sourceAddressForCopy={resolvedSourceAddress}
                    partyType={partyType}
                />,
                handleCloseModal ? handleCloseModal : closeModal
            );
        },
        [
            classifiers,
            watchedValue,
            handleCloseModal,
            addressCustomFields,
            isBillingAddress,
            sourceAddressForCopy,
            getSourceAddressForCopy,
            partyType,
            closeModal,
            initialAddressCustomFields,
            openModal,
        ]
    );

    const getAddressText = (address: AddressDto | null) => {
        if (!address) return '-';
        const countryLabel =
            classifiers?.['COUNTRY'] && address
                ? getClassifierText(classifiers?.['COUNTRY'], get(watchedValue, 'countryCode'))
                : undefined;
        return getAddress(address as AddressDto, countryLabel);
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field, fieldState: { error } }) => {
                const isEmpty = !field.value || !field.value.address1;
                const edit = () =>
                    openAddressModal(field.onChange as (event: AddressDto | React.ChangeEvent<Element> | null) => void);
                const clear = clearable && !isEmpty && (
                    <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => {
                            field.onChange(null);
                        }}
                        disabled={disabled}
                    >
                        <FormattedMessage id="clear" />
                    </Button>
                );
                return (
                    <InputWrapper
                        name={name}
                        label={label}
                        required={required}
                        isEmpty={isEmpty}
                        readonly={!!readonly}
                        rightLabelComponent={rightLabelComponent}
                        hasErrors={!!error}
                        error={readonly ? undefined : error}
                        className={cn(wideClass, wrapperClassName)}
                    >
                        {inSection && !readonly ? (
                            <div className="min-h-9 py-2 pr-[11px] text-sm font-medium break-words">
                                {getAddressText(field.value)}
                                {hasPrivilege ? (
                                    <>
                                        {clear ? (
                                            <span className="-my-1 ml-1 inline-flex align-middle">{clear}</span>
                                        ) : null}
                                        <AddressPencilButton ref={field.ref} onClick={edit} disabled={disabled} />
                                    </>
                                ) : null}
                            </div>
                        ) : (
                            <div
                                className={cn(
                                    'flex items-center justify-between text-sm',
                                    readonly ? 'min-h-5' : 'h-10'
                                )}
                            >
                                <div>{getAddressText(field.value)}</div>
                                {readonly ? (
                                    <div />
                                ) : hasPrivilege ? (
                                    <div className="flex gap-2">
                                        {clear}
                                        <Button
                                            ref={field.ref}
                                            type="button"
                                            variant="outline"
                                            size="xs"
                                            onClick={edit}
                                            disabled={disabled || readonly}
                                        >
                                            <FormattedMessage id="edit" />
                                        </Button>
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </InputWrapper>
                );
            }}
        />
    );
};

export default AddressInput;
